import { useEventCallback } from '@cian/react-utils';
import { ModalWindow } from '@cian/ui-kit';

import { useSelector } from 'react-redux';

import { RegionalDistrictsContentContainerLoadable } from './RegionalDistrictsContentContainer';
import { RegionalDistrictsSearchContainer } from './RegionalDistrictsSearchContainer';
import { useScrollToListElementById } from './hooks/useScrollToListElementById';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { FC, useCallback } from 'react';
import { ERegionalDistrictsStatus } from 'shared/types/geo';
import { SearchButtonContainer } from '../../SearchButtonContainer';
import { LoadableErrorBoundary } from '../../../../../../packages/LoadableHelpers';
import { selectRegionalDistrictsStatus } from 'shared/selectors/geo';
import { selectRegionalDistrict } from 'shared/thunks/geo/regionalDistricts';
import { useAppDispatch, useJsonQueryBackup } from 'shared/hooks';
import { RegionalDistrictsModalHeader, RegionalDistrictsModalFooter } from '../../../../../../packages/GeoComponents';
import { applyFilters } from 'shared/thunks/filters';
import { updateTags } from 'shared/thunks/tags';
import { updateLocalStorageFiltersWereApplied } from 'shared/utils/uxfbLocalStorage/uxfbLocalStorage';

interface IRegionalDistrictsModalContainerProps {
  onModalClose: VoidFunction;
}

export const RegionalDistrictsModalContainer: FC<IRegionalDistrictsModalContainerProps> = ({ onModalClose }) => {
  const { logger } = useApplicationContext();

  const dispatch = useAppDispatch();

  const status = useSelector(selectRegionalDistrictsStatus);

  const { resetJsonQuery } = useJsonQueryBackup();

  const { scrollToListElementById, listRefs } = useScrollToListElementById();

  const handleSearchSuggestSelect = useCallback(
    (id: number) => {
      dispatch(selectRegionalDistrict(id));
      scrollToListElementById(id);
    },
    [dispatch, scrollToListElementById],
  );

  const handleRetry = useEventCallback(() => RegionalDistrictsContentContainerLoadable.load());

  const handleClose = useCallback(() => {
    onModalClose();
    resetJsonQuery();
  }, [onModalClose, resetJsonQuery]);

  const handleApply = useCallback(() => {
    onModalClose();
    dispatch(applyFilters());
    dispatch(updateTags(true));
    updateLocalStorageFiltersWereApplied();
  }, [dispatch, onModalClose, updateLocalStorageFiltersWereApplied]);

  return (
    <ModalWindow
      fixed
      size="M"
      escape
      width="100%"
      maxWidth={928}
      height={684}
      open
      onClose={handleClose}
      header={
        <RegionalDistrictsModalHeader>
          {status === ERegionalDistrictsStatus.Ready && (
            <RegionalDistrictsSearchContainer onSelect={handleSearchSuggestSelect} />
          )}
        </RegionalDistrictsModalHeader>
      }
      footer={
        <RegionalDistrictsModalFooter>
          <SearchButtonContainer onClick={handleApply} />
        </RegionalDistrictsModalFooter>
      }
    >
      <LoadableErrorBoundary logger={logger} onRetry={handleRetry}>
        <RegionalDistrictsContentContainerLoadable refs={listRefs} />
      </LoadableErrorBoundary>
    </ModalWindow>
  );
};
